import { useCallback, useEffect, useState } from 'react'
import { Center } from '@astryxdesign/core/Center'
import { Spinner } from '@astryxdesign/core/Spinner'
import { Stack } from '@astryxdesign/core/Stack'
import { Banner } from '@astryxdesign/core/Banner'
import { Button } from '@astryxdesign/core/Button'
import { InternationalizationProvider } from '@astryxdesign/core/i18n'
import frFR from '@astryxdesign/core/locales/fr-FR.generated.js'
import ConsoleLogin from './ConsoleLogin'
import ConsoleHome from './ConsoleHome'
import { readMe, logout } from './consoleApi'

// padelr.app/console: the club admins' web console.
//
// Whether to show the login or the console is the server's answer to "me",
// asked on arrival and again whenever the tab comes back into view - that is
// what sends an admin back to the QR code once their 8 hours are up, or once
// they no longer administer anything.
//
// A call of the console's own that answers 401 asks the same question again
// rather than deciding by itself: "me" is what sends the page to the login,
// with the notice saying the session expired.
//
// Everything is in French, the design system's own words included - the
// labels of its close buttons, its date picker's months.
const MESSAGES = { 'fr-FR': frFR }

export default function Console() {
    return (
        <InternationalizationProvider locale="fr-FR" messages={MESSAGES}>
            <ConsoleView />
        </InternationalizationProvider>
    )
}

function ConsoleView() {
    const [view, setView] = useState({ name: 'loading' })
    const [isLoggingOut, setLoggingOut] = useState(false)

    const refresh = useCallback(async () => {
        let answer
        try {
            answer = await readMe()
        } catch (e) {
            setView({ name: 'offline' })
            return
        }

        if (answer.status === 200 && answer.json) {
            setView({ name: 'home', me: answer.json })
        } else if (answer.status === 403) {
            setView({ name: 'login', notice: "Vous n'administrez plus aucun club." })
        } else if (answer.status === 401) {
            setView(current => ({
                name: 'login',
                notice: current.name === 'home' ? 'Votre session a expiré. Reconnectez-vous.' : undefined,
            }))
        } else {
            setView({ name: 'offline' })
        }
    }, [])

    useEffect(() => {
        document.title = 'Console Padelr'
        refresh()
    }, [refresh])

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh()
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => document.removeEventListener('visibilitychange', onVisible)
    }, [refresh])

    const onLogout = async () => {
        setLoggingOut(true)
        try {
            await logout()
        } finally {
            setLoggingOut(false)
            setView({ name: 'login' })
        }
    }

    switch (view.name) {
        case 'home':
            return <ConsoleHome me={view.me} onLogout={onLogout} isLoggingOut={isLoggingOut} onSessionLost={refresh} />
        case 'login':
            return <ConsoleLogin notice={view.notice} onLoggedIn={refresh} />
        case 'offline':
            return (
                <Center axis="both" padding={6}>
                    <Stack gap={4} hAlign="center">
                        <Banner status="error" title="Le serveur ne répond pas." />
                        <Button label="Réessayer" variant="secondary" onClick={refresh} />
                    </Stack>
                </Center>
            )
        default:
            return (
                <Center axis="both" padding={6}>
                    <Spinner label="Chargement" />
                </Center>
            )
    }
}
